import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Card, CardContent,
} from '../components/ui/card';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Search, RefreshCw, Download, ExternalLink, Copy, X, Loader2,
  CheckCircle2, Clock, XCircle, Sparkles, ChevronLeft,
  ChevronRight, FileText, Server, Wrench, Building2, RotateCcw,
  MailCheck, Receipt, MessageCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUS_TONES = {
  paid:               { label: 'Paid',               icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  partially_refunded: { label: 'Partially refunded', icon: RotateCcw,    cls: 'bg-orange-500/15 text-orange-700 border-orange-500/30' },
  refunded:           { label: 'Refunded',           icon: RotateCcw,    cls: 'bg-slate-500/15 text-slate-700 border-slate-500/30' },
  created:            { label: 'Awaiting',           icon: Clock,        cls: 'bg-amber-500/10 text-amber-700 border-amber-500/30' },
  cancelled:          { label: 'Cancelled',          icon: XCircle,      cls: 'bg-red-500/10 text-red-600 border-red-500/30' },
  expired:            { label: 'Expired',            icon: Clock,        cls: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/30' },
  failed:             { label: 'Failed',             icon: XCircle,      cls: 'bg-red-500/10 text-red-600 border-red-500/30' },
};

const SERVICE_META = {
  hosting: { label: 'Hosting', icon: Server, cls: 'text-sky-600' },
  amc:     { label: 'AMC',     icon: Wrench, cls: 'text-violet-600' },
  smb:     { label: 'SMB',     icon: Building2, cls: 'text-orange-600' },
};

const SOURCE_KINDS = [
  { value: 'from_quote',     label: 'From quote' },
  { value: 'direct',         label: 'Direct' },
  { value: 'reminder_cron',  label: 'Reminder cron' },
];

const STATUS_OPTIONS = [
  { value: 'paid',               label: 'Paid' },
  { value: 'partially_refunded', label: 'Partially refunded' },
  { value: 'refunded',           label: 'Refunded' },
  { value: 'awaiting',           label: 'Awaiting' },
  { value: 'cancelled',          label: 'Cancelled' },
  { value: 'expired',            label: 'Expired' },
  { value: 'failed',             label: 'Failed' },
];

const formatINR = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(Number(n) || 0);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const daysAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const StatusChip = ({ status }) => {
  const meta = STATUS_TONES[status] || STATUS_TONES.created;
  const Icon = meta.icon;
  return (
    <Badge className={`text-[10px] ${meta.cls} flex items-center gap-1 w-fit`} data-testid={`status-${status}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
};

const ServiceChip = ({ service }) => {
  const m = SERVICE_META[service] || { label: service, icon: FileText, cls: '' };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${m.cls}`}>
      <Icon className="h-3 w-3" />
      {m.label}
    </span>
  );
};

// ---------------------------------------------------------------- main page

export const PaymentLinksPage = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('payment_links.edit') || hasPermission('*');

  // ---- Filter state ------------------------------------------------------
  const [filters, setFilters] = useState(() => {
    // 30-day default window so the page feels populated on first load.
    const today = new Date();
    const from = new Date(Date.now() - 30 * 86400000);
    const iso = (d) => d.toISOString().slice(0, 10);
    return {
      service: 'all',
      status: [],
      source_kind: [],
      date_from: iso(from),
      date_to: iso(today),
      search: '',
      amount_min: '',
      amount_max: '',
    };
  });
  const [page, setPage] = useState(1);
  const limit = 50;

  // ---- Data state --------------------------------------------------------
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  // ---- Selection + drawer state -----------------------------------------
  const [selected, setSelected] = useState(new Set());
  const [drawerRow, setDrawerRow] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const filterParams = useMemo(() => {
    const p = {};
    if (filters.service && filters.service !== 'all') p.service = filters.service;
    if (filters.status.length) p.status = filters.status.join(',');
    if (filters.source_kind.length) p.source_kind = filters.source_kind.join(',');
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.search.trim()) p.search = filters.search.trim();
    if (filters.amount_min) p.amount_min = filters.amount_min;
    if (filters.amount_max) p.amount_max = filters.amount_max;
    return p;
  }, [filters]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/payment-links/all', {
        params: { ...filterParams, page, limit },
      });
      setRows(r.data?.data || []);
      setTotal(r.data?.total || 0);
      setPages(r.data?.pages || 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payment links');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get('/payment-links/stats', {
        params: {
          date_from: filterParams.date_from,
          date_to: filterParams.date_to,
        },
      });
      setStats(r.data?.data || null);
    } catch {
      setStats(null);
    }
  }, [filterParams.date_from, filterParams.date_to]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Reset selection whenever filters/page change — selected ids may no
  // longer be on the visible page.
  useEffect(() => { setSelected(new Set()); }, [filterParams, page]);

  // ---- Filter helpers ----------------------------------------------------

  const toggleStatus = (s) => {
    setFilters(f => ({
      ...f,
      status: f.status.includes(s) ? f.status.filter(x => x !== s) : [...f.status, s],
    }));
    setPage(1);
  };

  const toggleSource = (s) => {
    setFilters(f => ({
      ...f,
      source_kind: f.source_kind.includes(s) ? f.source_kind.filter(x => x !== s) : [...f.source_kind, s],
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      service: 'all', status: [], source_kind: [],
      date_from: '', date_to: '', search: '',
      amount_min: '', amount_max: '',
    });
    setPage(1);
  };

  // ---- Bulk actions ------------------------------------------------------

  const allOnPageSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const toggleAll = () => {
    if (allOnPageSelected) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };
  const toggleOne = (id) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const bulkSync = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    const tid = toast.loading(`Syncing ${selected.size} link(s) with Razorpay…`);
    try {
      const r = await api.post('/payment-links/bulk-sync', {
        ids: Array.from(selected),
      });
      toast.success(`Synced ${r.data?.scanned}. ${r.data?.changed} status update(s).`, { id: tid });
      await fetchRows();
      await fetchStats();
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk sync failed', { id: tid });
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCSV = async () => {
    const tid = toast.loading('Generating CSV export…');
    try {
      const r = await api.get('/payment-links/export', {
        params: filterParams,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment-links-${filterParams.date_from || 'all'}_${filterParams.date_to || 'today'}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('CSV downloaded', { id: tid });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed', { id: tid });
    }
  };

  const syncOne = async (row) => {
    if (row.source === 'smb_order') {
      // SMB orders sync via the same bulk endpoint (it handles both shapes).
      const tid = toast.loading('Refreshing from Razorpay…');
      try {
        const r = await api.post('/payment-links/bulk-sync', { ids: [row.id] });
        toast.success(r.data?.changed ? 'Status updated.' : 'No change yet.', { id: tid });
        fetchRows(); fetchStats();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Sync failed', { id: tid });
      }
      return;
    }
    const tid = toast.loading('Refreshing from Razorpay…');
    try {
      const r = await api.post(`/payment-links/${row.id}/sync`);
      const updated = r.data?.data || {};
      if (r.data?.changed) {
        toast.success(`Status updated to "${updated.status}".`, { id: tid });
      } else {
        toast.message('No change yet.', { id: tid });
      }
      fetchRows(); fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed', { id: tid });
    }
  };

  /**
   * Open the auto-generated Tax Invoice PDF for this payment link.
   * Triggers a server-side regenerate so the file is always current
   * (covers cases where the cron generated the row but PDF rendering
   * had failed on the original webhook tick) then streams the bytes.
   */
  const viewInvoicePdf = async (row) => {
    if (!row.renewal_invoice_id) return;
    const tid = toast.loading('Opening tax invoice…');
    try {
      await api.post(`/invoices/${row.renewal_invoice_id}/regenerate-pdf`);
      const resp = await api.get(`/invoices/${row.renewal_invoice_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      toast.success('Tax invoice opened', { id: tid });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open PDF', { id: tid });
    }
  };

  // ---- Render ------------------------------------------------------------

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="payment-links-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Payment Links</h1>
          <p className="text-xs text-muted-foreground">
            Unified view of all Razorpay payment artifacts — Hosting & AMC renewals, SMB activation orders.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchRows(); fetchStats(); }} data-testid="payment-links-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="payment-links-export">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* ---- Stat strip ---- */}
      <StatStrip stats={stats} />

      {/* ---- Filter bar ---- */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1 flex-1 min-w-[200px]">
              <Label className="text-[11px]">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={filters.search}
                  onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
                  placeholder="Razorpay id, email, account, URL…"
                  className="pl-7 h-8 text-xs"
                  data-testid="payment-links-search"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Service</Label>
              <Select value={filters.service} onValueChange={(v) => { setFilters(f => ({ ...f, service: v })); setPage(1); }}>
                <SelectTrigger className="h-8 text-xs w-32" data-testid="payment-links-service-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="hosting">Hosting</SelectItem>
                  <SelectItem value="amc">AMC</SelectItem>
                  <SelectItem value="smb">SMB</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">From</Label>
              <Input type="date" value={filters.date_from}
                     onChange={(e) => { setFilters(f => ({ ...f, date_from: e.target.value })); setPage(1); }}
                     className="h-8 text-xs w-36" data-testid="payment-links-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">To</Label>
              <Input type="date" value={filters.date_to}
                     onChange={(e) => { setFilters(f => ({ ...f, date_to: e.target.value })); setPage(1); }}
                     className="h-8 text-xs w-36" data-testid="payment-links-date-to" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Min ₹</Label>
              <Input type="number" value={filters.amount_min}
                     onChange={(e) => { setFilters(f => ({ ...f, amount_min: e.target.value })); setPage(1); }}
                     className="h-8 text-xs w-24" placeholder="0" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Max ₹</Label>
              <Input type="number" value={filters.amount_max}
                     onChange={(e) => { setFilters(f => ({ ...f, amount_max: e.target.value })); setPage(1); }}
                     className="h-8 text-xs w-24" placeholder="—" />
            </div>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[11px] h-8" data-testid="payment-links-clear-filters">
              Clear
            </Button>
          </div>
          {/* Chip-style multi-selects for status + source kind */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground mr-1">Status</span>
              {STATUS_OPTIONS.map(s => (
                <button key={s.value} type="button"
                        onClick={() => toggleStatus(s.value)}
                        data-testid={`filter-status-${s.value}`}
                        className={`text-[11px] px-2 py-0.5 rounded-md border transition-colors ${filters.status.includes(s.value)
                          ? 'bg-primary/15 border-primary/40 text-primary'
                          : 'bg-background border-border text-muted-foreground hover:bg-muted'}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground mr-1">Source</span>
              {SOURCE_KINDS.map(s => (
                <button key={s.value} type="button"
                        onClick={() => toggleSource(s.value)}
                        data-testid={`filter-source-${s.value}`}
                        className={`text-[11px] px-2 py-0.5 rounded-md border transition-colors ${filters.source_kind.includes(s.value)
                          ? 'bg-primary/15 border-primary/40 text-primary'
                          : 'bg-background border-border text-muted-foreground hover:bg-muted'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---- Bulk action bar (visible when something is selected) ---- */}
      {selected.size > 0 && (
        <Card className="bg-primary/5 border-primary/30">
          <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium" data-testid="bulk-selection-count">
              {selected.size} selected
            </span>
            <div className="flex items-center gap-2">
              {canEdit && (
                <Button size="sm" onClick={bulkSync} disabled={bulkBusy} data-testid="bulk-sync-btn">
                  {bulkBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Sync selected
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn">
                <X className="h-3.5 w-3.5 mr-1.5" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---- Results table ---- */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAll} data-testid="select-all" />
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Account · Client</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-muted-foreground text-sm">
                    No payment links match these filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map(r => (
                <TableRow key={r.id} className="hover:bg-muted/30 cursor-pointer"
                          data-testid={`paylink-row-${r.id}`}
                          onClick={() => setDrawerRow(r)}>
                  <TableCell onClick={(e) => e.stopPropagation()} className="w-8">
                    <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleOne(r.id)}
                              data-testid={`select-${r.id}`} />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={r.status} />
                    {r.renewal_email_status === 'sent' && (
                      <Badge
                        variant="outline"
                        className="mt-0.5 text-[9px] px-1 py-0 h-4 bg-sky-500/10 text-sky-700 border-sky-500/30 flex items-center gap-0.5 w-fit"
                        title="Tax invoice auto-emailed to client"
                        data-testid={`auto-emailed-${r.id}`}
                      >
                        <MailCheck className="h-2.5 w-2.5" />
                        Auto-emailed
                      </Badge>
                    )}
                    {r.whatsapp_status === 'sent' && (
                      <Badge
                        variant="outline"
                        className="mt-0.5 text-[9px] px-1 py-0 h-4 bg-emerald-500/10 text-emerald-700 border-emerald-500/30 flex items-center gap-0.5 w-fit"
                        title="WhatsApp reminder sent to client"
                        data-testid={`whatsapp-sent-${r.id}`}
                      >
                        <MessageCircle className="h-2.5 w-2.5" />
                        WhatsApp sent
                      </Badge>
                    )}
                    {r.whatsapp_status === 'failed' && (
                      <Badge
                        variant="outline"
                        className="mt-0.5 text-[9px] px-1 py-0 h-4 bg-red-500/10 text-red-700 border-red-500/30 flex items-center gap-0.5 w-fit"
                        title={r.whatsapp_error || 'WhatsApp send failed'}
                        data-testid={`whatsapp-failed-${r.id}`}
                      >
                        <MessageCircle className="h-2.5 w-2.5" />
                        WA failed
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{formatDate(r.created_at)}</div>
                    <div className="text-[10px] text-muted-foreground">{daysAgo(r.created_at)}</div>
                  </TableCell>
                  <TableCell><ServiceChip service={r.service} /></TableCell>
                  <TableCell className="text-xs max-w-[200px]">
                    <div className="font-medium truncate">{r.entity_label || '—'}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {r.client_name || (r.service === 'smb' ? 'SMB Lead' : '—')}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="font-semibold text-sm">{formatINR(r.amount)}</div>
                    {r.tenure_years && (
                      <div className="text-[10px] text-muted-foreground">{r.tenure_years}y</div>
                    )}
                    {r.amount_refunded > 0 && (
                      <div className="text-[10px] text-orange-600 font-medium" data-testid={`refunded-${r.id}`}>
                        −{formatINR(r.amount_refunded)} refunded
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.from_quote_id ? (
                      <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700">
                        <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                        From quote
                      </Badge>
                    ) : r.created_by === 'system_cron' ? (
                      <span className="text-[10px] text-muted-foreground">Reminder cron</span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">Direct</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs max-w-[160px] truncate">{r.recipient_email || '—'}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {r.renewal_invoice_id && (
                        <Button size="icon" variant="ghost" onClick={() => viewInvoicePdf(r)}
                                title={`View tax invoice ${r.renewal_invoice_number || ''}`.trim()}
                                data-testid={`view-invoice-${r.id}`}>
                          <Receipt className="h-3.5 w-3.5 text-emerald-600" />
                        </Button>
                      )}
                      {r.short_url && (
                        <Button size="icon" variant="ghost" asChild title="Open Razorpay link" data-testid={`open-${r.id}`}>
                          <a href={r.short_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
                        </Button>
                      )}
                      {canEdit && (r.status === 'created' || r.status === 'paid' || r.status === 'partially_refunded') && (
                        <Button size="icon" variant="ghost" onClick={() => syncOne(r)}
                                title={r.status === 'created' ? 'Sync with Razorpay' : 'Re-check refund status'}
                                data-testid={`sync-${r.id}`}>
                          <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ---- Pagination ---- */}
      {pages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid="pagination-info">
            Page {page} of {pages} · {total} total
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="prev-page">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))} data-testid="next-page">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* ---- Detail drawer ---- */}
      <Sheet open={!!drawerRow} onOpenChange={(o) => { if (!o) setDrawerRow(null); }}>
        <SheetContent className="sm:max-w-md overflow-y-auto" data-testid="paylink-drawer">
          {drawerRow && <DetailDrawer row={drawerRow}
                                      onSync={() => { syncOne(drawerRow); }}
                                      onViewInvoice={() => { viewInvoicePdf(drawerRow); }}
                                      canEdit={canEdit} />}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// ---------------------------------------------------------------- pieces

const StatStrip = ({ stats }) => {
  const tiles = [
    { key: 'collected',       label: 'Collected',         value: stats ? formatINR(stats.collected) : '—',
      sub: stats ? `${stats.paid_count || 0} payments` : '', cls: 'text-emerald-600' },
    { key: 'awaiting',        label: 'Awaiting',          value: stats ? formatINR(stats.awaiting) : '—',
      sub: stats ? `${stats.total_count - (stats.paid_count || 0) - (stats.cancelled_count || 0) - (stats.expired_count || 0)} open` : '',
      cls: 'text-amber-600' },
    { key: 'cancelled',       label: 'Cancelled',         value: stats ? stats.cancelled_count : '—',
      sub: 'count',           cls: 'text-red-600' },
    { key: 'expired',         label: 'Expired',           value: stats ? stats.expired_count : '—',
      sub: 'count',           cls: 'text-zinc-500' },
    { key: 'avg_days',        label: 'Avg. days to pay',  value: stats?.avg_days_to_pay != null ? `${stats.avg_days_to_pay} d` : '—',
      sub: 'paid links',      cls: 'text-sky-600' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {tiles.map(t => (
        <Card key={t.key} data-testid={`stat-${t.key}`}>
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{t.label}</p>
            <p className={`text-lg font-bold mt-0.5 ${t.cls}`}>{t.value}</p>
            <p className="text-[10px] text-muted-foreground">{t.sub}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const DetailDrawer = ({ row, onSync, onViewInvoice, canEdit }) => {
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error('Copy failed'); }
  };
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <ServiceChip service={row.service} />
          <span>{row.entity_label || '—'}</span>
        </SheetTitle>
        {/* Description rendered as <div> not <SheetDescription>; the
            shadcn component renders <p> which can't legally wrap the
            <div>-based Badge inside <StatusChip>. */}
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <StatusChip status={row.status} />
          <span className="text-xs">· {formatINR(row.amount)}{row.tenure_years ? ` · ${row.tenure_years}y` : ''}</span>
        </div>
      </SheetHeader>
      <div className="space-y-4 mt-4 text-sm">
        <FieldRow label="Created"            value={formatDate(row.created_at)} />
        {row.paid_at && <FieldRow label="Paid at" value={formatDate(row.paid_at)} />}
        {row.extended_to_date && <FieldRow label="Renewed to" value={row.extended_to_date} valueCls="text-emerald-600 font-medium" />}
        {row.cancelled_at && <FieldRow label="Cancelled at" value={formatDate(row.cancelled_at)} />}
        {row.amount_refunded > 0 && (
          <FieldRow
            label="Refunded"
            value={
              <span className="text-orange-600 font-medium">
                −{formatINR(row.amount_refunded)}
                {row.status === 'partially_refunded' && (
                  <span className="text-[10px] text-muted-foreground ml-1">
                    (of {formatINR(row.amount)})
                  </span>
                )}
              </span>
            }
          />
        )}
        {row.refund_synced_at && (
          <FieldRow label="Refund synced" value={formatDate(row.refund_synced_at)} />
        )}

        <div className="border-t pt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Recipient</p>
          <FieldRow label="Name"  value={row.recipient_name || '—'} />
          <FieldRow label="Email" value={row.recipient_email || '—'} />
          {row.recipient_phone && <FieldRow label="Phone" value={row.recipient_phone} />}
          {row.client_name && (
            <FieldRow label="Client" value={
              row.client_id ? (
                <Link to={`/clients/${row.client_id}`} className="text-primary hover:underline">{row.client_name}</Link>
              ) : row.client_name
            } />
          )}
        </div>

        <div className="border-t pt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Razorpay</p>
          {row.razorpay_link_id && (
            <FieldRow label="Link ID" value={
              <button onClick={() => copy(row.razorpay_link_id, 'Link ID')}
                      className="font-mono text-[11px] hover:text-primary flex items-center gap-1">
                {row.razorpay_link_id}
                <Copy className="h-2.5 w-2.5 opacity-50" />
              </button>
            } />
          )}
          {row.razorpay_order_id && (
            <FieldRow label="Order ID" value={
              <button onClick={() => copy(row.razorpay_order_id, 'Order ID')}
                      className="font-mono text-[11px] hover:text-primary flex items-center gap-1">
                {row.razorpay_order_id}
                <Copy className="h-2.5 w-2.5 opacity-50" />
              </button>
            } />
          )}
          {row.razorpay_payment_id && (
            <FieldRow label="Payment ID" value={
              <button onClick={() => copy(row.razorpay_payment_id, 'Payment ID')}
                      className="font-mono text-[11px] hover:text-primary flex items-center gap-1">
                {row.razorpay_payment_id}
                <Copy className="h-2.5 w-2.5 opacity-50" />
              </button>
            } />
          )}
          {row.short_url && (
            <FieldRow label="Short URL" value={
              <a href={row.short_url} target="_blank" rel="noopener noreferrer"
                 className="font-mono text-[11px] text-primary hover:underline flex items-center gap-1">
                {row.short_url}
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
            } />
          )}
        </div>

        {row.renewal_invoice_id && (
          <div className="border-t pt-3 space-y-2" data-testid="drawer-tax-invoice">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Tax Invoice (Auto-generated)</p>
            <FieldRow label="Invoice No." value={
              <Link to="/invoices" className="font-mono text-[11px] text-primary hover:underline">
                {row.renewal_invoice_number || row.renewal_invoice_id}
              </Link>
            } />
            <FieldRow label="Email" value={
              row.renewal_email_status === 'sent' ? (
                <span className="text-emerald-600 font-medium flex items-center gap-1 justify-end">
                  <MailCheck className="h-3 w-3" /> Sent to client
                </span>
              ) : row.renewal_email_status === 'skipped_no_pdf' ? (
                <span className="text-amber-600">Pending — PDF retry queued</span>
              ) : row.renewal_email_status === 'failed' ? (
                <span className="text-red-600">Send failed</span>
              ) : (
                <span className="text-muted-foreground">{row.renewal_email_status || '—'}</span>
              )
            } />
            <Button size="sm" variant="outline" onClick={onViewInvoice} className="w-full mt-1"
                    data-testid="drawer-view-invoice-btn">
              <Receipt className="h-3.5 w-3.5 mr-1.5" />
              View Tax Invoice PDF
            </Button>
          </div>
        )}

        {/* WhatsApp reminder status — present on every link, not just
            renewals. Stamped at create-time by `send_renewal_whatsapp`. */}
        {row.whatsapp_status && (
          <div className="border-t pt-3 space-y-2" data-testid="drawer-whatsapp">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">WhatsApp Reminder</p>
            <FieldRow label="Status" value={
              row.whatsapp_status === 'sent' ? (
                <span className="text-emerald-600 font-medium flex items-center gap-1 justify-end">
                  <MessageCircle className="h-3 w-3" /> Sent
                </span>
              ) : row.whatsapp_status === 'failed' ? (
                <span className="text-red-600" title={row.whatsapp_error || ''}>Send failed</span>
              ) : row.whatsapp_status === 'no_phone' ? (
                <span className="text-amber-600">Skipped — no phone on file</span>
              ) : row.whatsapp_status === 'disabled' ? (
                <span className="text-muted-foreground">Disabled in Settings</span>
              ) : (
                <span className="text-muted-foreground">{row.whatsapp_status}</span>
              )
            } />
            {row.whatsapp_sent_at && (
              <FieldRow label="Sent at" value={
                <span className="text-[11px] text-muted-foreground">
                  {new Date(row.whatsapp_sent_at).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </span>
              } />
            )}
          </div>
        )}

        <div className="border-t pt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Source / Audit</p>
          <FieldRow label="Source" value={
            row.from_quote_id ? `From quote (${row.from_quote_tenure_years || '?'}y)` :
            row.created_by === 'system_cron' ? 'Reminder cron' : 'Direct'
          } />
          {row.purpose && <FieldRow label="Purpose" value={row.purpose} />}
          {row.description && <FieldRow label="Description" value={row.description} />}
        </div>

        <div className="border-t pt-3 flex gap-2">
          {canEdit && (row.status === 'created' || row.status === 'paid' || row.status === 'partially_refunded') && (
            <Button size="sm" variant="outline" onClick={onSync} data-testid="drawer-sync-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              {row.status === 'created' ? 'Sync with Razorpay' : 'Re-check refund status'}
            </Button>
          )}
          {row.service === 'hosting' && row.entity_id && (
            <Button size="sm" variant="ghost" asChild>
              <Link to={`/hosting`}><Server className="h-3.5 w-3.5 mr-1.5" />Open Hosting</Link>
            </Button>
          )}
          {row.service === 'amc' && row.entity_id && (
            <Button size="sm" variant="ghost" asChild>
              <Link to={`/amc`}><Wrench className="h-3.5 w-3.5 mr-1.5" />Open AMC</Link>
            </Button>
          )}
          {row.service === 'smb' && row.entity_id && (
            <Button size="sm" variant="ghost" asChild>
              <Link to={`/smb?lead=${row.entity_id}`}><Building2 className="h-3.5 w-3.5 mr-1.5" />Open Lead</Link>
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

const FieldRow = ({ label, value, valueCls = '' }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-[11px] text-muted-foreground shrink-0">{label}</span>
    <span className={`text-xs text-right break-all ${valueCls}`}>{value}</span>
  </div>
);

export default PaymentLinksPage;
